import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import "../../../UserDashboard/components/UserDashboardHome/UserDashboardHome.css";
import localeText from "../../../../locale";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import AdminService from "../../../../service/AdminService";
import { FilterMatchMode } from "primereact/api";
import { formatDate } from "../../../../utilities/formatDate";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { InputSwitch } from "primereact/inputswitch";

const PaidSubscriptionsReport = ({ canManageInvoicing = false }) => {
  const [loading, setLoading] = useState(false);
  const [paidSubscriptions, setPaidSubscriptions] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [invoicingStatus, setInvoicingStatus] = useState(null);
  const [invoicingStatusLoading, setInvoicingStatusLoading] = useState(false);
  const [invoicingToggleLoading, setInvoicingToggleLoading] = useState(false);

  const adminService = new AdminService();
  const toast = useRef(null);

  const changeTypePaymentPlan = (value) => {
    if (value === "FIRST") return localeText("initialSub");
    if (value === "RENEW") return localeText("annualRenewal");
    if (value === "TRIAL") return localeText("test");
    return value;
  };

  async function fetchInvoicingStatus() {
    try {
      setInvoicingStatusLoading(true);
      const res = await adminService.getInvoicingStatus(
        localStorage.getItem("__extraconomy_token"),
      );
      setInvoicingStatus(res);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorFetchingInvoicingStatus"),
        life: 6000,
      });
    } finally {
      setInvoicingStatusLoading(false);
    }
  }

  useEffect(() => {
    if (canManageInvoicing) fetchInvoicingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageInvoicing]);

  const handleToggleInvoicing = async (checked) => {
    if (checked && !invoicingStatus?.result?.enableAllowed) return;

    try {
      setInvoicingToggleLoading(true);
      const token = localStorage.getItem("__extraconomy_token");
      const res = checked
        ? await adminService.enableInvoicing(token)
        : await adminService.disableInvoicing(token);

      if (res?.messageCode && res.messageCode !== 200000 && res.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail:
            res.message ||
            localeText("data").find((msg) => msg.code === res.messageCode)?.message ||
            localeText("errorTogglingInvoicing"),
          life: 6000,
        });
      } else {
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: checked
            ? localeText("invoicingEnabledSuccessfully")
            : localeText("invoicingDisabledSuccessfully"),
          life: 4000,
        });
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      const messageCode = e.response?.data?.messageCode;
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail:
          e.response?.data?.message ||
          localeText("data").find((msg) => msg.code === messageCode)?.message ||
          localeText("errorTogglingInvoicing"),
        life: 6000,
      });
    } finally {
      setInvoicingToggleLoading(false);
      fetchInvoicingStatus();
    }
  };

  const invoiceStatusBodyTemplate = (subscription) => {
    const { invoiceStatus, invoiceNumber, invoiceYear, invoiceErrorMessage } = subscription;

    if (!invoiceStatus) return <span>—</span>;

    if (invoiceStatus === "SENT") {
      return <Tag severity="success" value={`WEB-${invoiceNumber}/${invoiceYear}`} />;
    }

    if (invoiceStatus === "PENDING") {
      return <Tag severity="warning" value={localeText("invoiceStatusPending")} />;
    }

    if (invoiceStatus === "ERROR") {
      return (
        <Tag
          severity="danger"
          value={localeText("invoiceStatusError")}
          className="invoice-error-tag"
          data-pr-tooltip={invoiceErrorMessage}
          data-pr-position="top"
        />
      );
    }

    return <span>—</span>;
  };

  const filters = {
    firstname: { value: null, matchMode: FilterMatchMode.CONTAINS },
    surname: { value: null, matchMode: FilterMatchMode.CONTAINS },
    address: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partitaivaOrCodicefiscale: { value: null, matchMode: FilterMatchMode.CONTAINS },
    paymentMethod: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };

  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [dateBegin, setDateBegin] = useState("2000-01-01");
  const [dateEnd, setDateEnd] = useState(formatDateForApi(new Date()));

  async function fetchData(dateBeginParam = dateBegin, dateEndParam = dateEnd) {
    if (dateBeginParam > dateEndParam) {
      toast.current.show({
        severity: "warn",
        summary: localeText("error"),
        detail: "A data inicial deve ser menor ou igual a data final",
        life: 5000,
      });
      return;
    }

    try {
      setLoading(true);
      const res = await adminService.getPaidSubscriptionsReport(
        localStorage.getItem("__extraconomy_token"),
        dateBeginParam,
        dateEndParam,
      );

      if (res.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: res.message || localeText("errorFetchingData"),
          life: 6000,
        });
        setLoading(false);
        return;
      }

      setPaidSubscriptions(res.result || []);
      setLoading(false);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorFetchingData"),
        life: 6000,
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    fetchData(dateBegin, dateEnd);
  };

  const handleDownloadPdf = async () => {
    try {
      if (dateBegin > dateEnd) {
        toast.current.show({
          severity: "warn",
          summary: localeText("error"),
          detail: "A data inicial deve ser menor ou igual a data final",
          life: 5000,
        });
        return;
      }

      setDownloadingPdf(true);
      const response = await adminService.getPaidSubscriptionsReportPdf(
        localStorage.getItem("__extraconomy_token"),
        dateBegin,
        dateEnd,
      );

      if (response && response.data) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const today = new Date();
        const dateStr = today.toISOString().split("T")[0];
        link.download = `paid-subscriptions-report-${dateStr}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("pdfDownloadedSuccessfully"),
          life: 3000,
        });
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorDownloadingPdf"),
        life: 6000,
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const emptyMessage = () => {
    if (loading) return null;

    return (
      <div className="p-text-center">
        <i className="pi pi-inbox" style={{ fontSize: "3rem" }}></i>
        <p className="text-400 mt-3">{localeText("noData")}</p>
      </div>
    );
  };

  const isToggleBlocked =
    !invoicingStatus?.result?.enabled && !invoicingStatus?.result?.enableAllowed;

  return (
    <div className="card border-1 border-300">
      <Toast ref={toast} />
      <Tooltip target=".invoice-error-tag" />

      <div className="flex justify-content-between align-items-center my-4">
        <h3>{localeText("paidSubscriptionsReport")}</h3>
        <Button
          label={localeText("exportData")}
          icon="pi pi-download"
          onClick={handleDownloadPdf}
          loading={downloadingPdf}
          disabled={downloadingPdf || loading}
          className="p-button-sm"
        />
      </div>
      <hr />

      {canManageInvoicing && (
        <>
          <div className="flex align-items-center justify-content-between gap-3 my-4 p-3 border-1 border-300 border-round">
            <div>
              <h4 className="mt-0 mb-1">{localeText("automaticInvoicing")}</h4>
              <p className="text-600 mt-0 mb-0">{localeText("automaticInvoicingDescription")}</p>
              {invoicingStatus?.result?.blockedReason && (
                <p className="text-orange-600 mt-2 mb-0 flex align-items-start gap-2">
                  <i className="pi pi-exclamation-triangle mt-1"></i>
                  <span>{invoicingStatus.result.blockedReason}</span>
                </p>
              )}
            </div>

            <Tooltip target=".invoicing-toggle-wrapper" />
            <span
              className="invoicing-toggle-wrapper"
              data-pr-tooltip={
                invoicingStatus?.result?.enableAllowed
                  ? undefined
                  : invoicingStatus?.result?.blockedReason
              }
              data-pr-position="top"
              style={{
                display: "inline-block",
                cursor: invoicingStatus?.result?.enableAllowed ? "default" : "not-allowed",
              }}
            >
              <InputSwitch
                checked={invoicingStatus?.result?.enabled}
                onChange={(e) => handleToggleInvoicing(e.value)}
                disabled={invoicingStatusLoading || invoicingToggleLoading || isToggleBlocked}
                style={{ pointerEvents: isToggleBlocked ? "none" : "auto" }}
              />
            </span>
          </div>
        </>
      )}

      <div className="flex flex-wrap align-items-end gap-3 my-4">
        <div className="flex flex-column gap-2">
          <label htmlFor="dateBegin">{localeText("startDate")}</label>
          <input
            id="dateBegin"
            type="date"
            value={dateBegin}
            onChange={(event) => setDateBegin(event.target.value)}
            className="p-inputtext p-component"
          />
        </div>

        <div className="flex flex-column gap-2">
          <label htmlFor="dateEnd">{localeText("endDate")}</label>
          <input
            id="dateEnd"
            type="date"
            value={dateEnd}
            onChange={(event) => setDateEnd(event.target.value)}
            className="p-inputtext p-component"
          />
        </div>

        <Button
          label={localeText("filter")}
          icon="pi pi-filter"
          onClick={handleApplyFilter}
          disabled={loading}
          className="p-button-sm"
        />
      </div>

      <DataTable
        className="mt-4 mb-6 p-datatable-customers"
        value={paidSubscriptions}
        paginator
        responsiveLayout="scroll"
        loading={loading}
        dataKey="id"
        filters={filters}
        filterDisplay="row"
        emptyMessage={emptyMessage()}
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={localeText("showingFirstToLast")}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
      >
        <Column
          header={localeText("name")}
          field="firstname"
          filter
          filterPlaceholder={localeText("searchByName")}
          sortable
          style={{ width: "12%" }}
        />

        <Column
          header={localeText("surname")}
          field="surname"
          filter
          filterPlaceholder={localeText("searchBySurname")}
          sortable
          style={{ width: "12%" }}
        />

        <Column
          header={localeText("taxId")}
          field="partitaivaOrCodicefiscale"
          filter
          filterPlaceholder={localeText("searchByDocument")}
          sortable
          style={{ width: "14%" }}
        />

        <Column
          header={localeText("address")}
          field="address"
          filter
          filterPlaceholder={localeText("searchByAddress")}
          sortable
          style={{ width: "12%" }}
        />

        <Column
          header={localeText("date")}
          field="date"
          dataType="date"
          body={(subscription) => formatDate(new Date(subscription.date))}
          sortable
          style={{ width: "10%" }}
        />

        <Column
          header={localeText("paymentMethod")}
          field="paymentMethod"
          filter
          filterPlaceholder={localeText("searchByPaymentMethod")}
          sortable
          style={{ width: "10%" }}
        />

        <Column
          header={localeText("amount")}
          field="amountPaid"
          sortable
          style={{ width: "10%" }}
        />

        <Column
          header={localeText("planType")}
          body={(subscription) => changeTypePaymentPlan(subscription.typePaymentPlan)}
          sortable
          style={{ width: "10%" }}
        />

        <Column
          header={localeText("invoiceStatus")}
          field="invoiceStatus"
          body={invoiceStatusBodyTemplate}
          sortable
          style={{ width: "10%" }}
        />
      </DataTable>
    </div>
  );
};

export default PaidSubscriptionsReport;
